import React, { useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { BarChart3, Bell, Home, LogOut, Menu, Shield, User, Waves, X } from 'lucide-react'
import { useLogout } from '../../hooks/useLogout'
import { useAuthContext } from '../../hooks/useAuthContext'
import { useNotificationContext } from '../../hooks/useNotificationContext'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type NavItem = { to: string; label: string; icon: typeof Home; badgeCount?: number }

function NavLinkRow({
  item,
  active,
  onNavigate,
  wide,
}: {
  item: NavItem
  active: boolean
  onNavigate?: () => void
  wide?: boolean
}) {
  const Icon = item.icon
  return (
    <Button
      asChild
      variant={active ? 'secondary' : 'ghost'}
      size="sm"
      className={cn(
        'gap-2 px-4 py-2.5 text-[15px] leading-snug text-zinc-100 hover:bg-zinc-800 hover:text-white',
        active && 'bg-zinc-800 text-white',
        wide && 'w-full justify-start'
      )}
    >
      <Link
        to={item.to}
        className={cn('inline-flex items-center gap-2.5 no-underline', wide && 'w-full')}
        onClick={onNavigate}
      >
        <Icon className="size-4 shrink-0 opacity-90" aria-hidden />
        <span>{item.label}</span>
        {item.badgeCount && item.badgeCount > 0 ? (
          <span className="ml-1 inline-flex min-w-5 items-center justify-center rounded-full bg-emerald-600 px-1.5 text-[11px] font-semibold leading-5 text-white">
            {item.badgeCount > 99 ? '99+' : item.badgeCount}
          </span>
        ) : null}
      </Link>
    </Button>
  )
}

const NAV_MOBILE_PORTAL_ID = 'hydro-nav-mobile-portal'

function getOrCreateNavPortalRoot(): HTMLElement | null {
  if (typeof document === 'undefined') return null
  let el = document.getElementById(NAV_MOBILE_PORTAL_ID)
  if (!el) {
    el = document.createElement('div')
    el.id = NAV_MOBILE_PORTAL_ID
    /** Outside <body> so iOS “fixed” menu isn’t tied to a scrolled / position:fixed body. */
    document.documentElement.appendChild(el)
  }
  return el
}

const Navbar1 = () => {
  const { logout } = useLogout()
  const { user } = useAuthContext()
  const { activeCount } = useNotificationContext()
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [navPortalEl, setNavPortalEl] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setNavPortalEl(getOrCreateNavPortalRoot())
  }, [])

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  /** iOS: after page scroll, overflow:hidden alone breaks fixed overlays; freeze body and restore scrollY on close. */
  useLayoutEffect(() => {
    if (!mobileOpen) return
    const scrollY = window.scrollY || document.documentElement.scrollTop || 0
    const body = document.body
    const html = document.documentElement
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
    }
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.left = '0'
    body.style.right = '0'
    body.style.width = '100%'
    body.style.overflow = 'hidden'
    html.style.overflow = 'hidden'

    return () => {
      body.style.position = prev.position
      body.style.top = prev.top
      body.style.left = prev.left
      body.style.right = prev.right
      body.style.width = prev.width
      body.style.overflow = prev.overflow
      html.style.overflow = prev.htmlOverflow
      window.scrollTo(0, scrollY)
    }
  }, [mobileOpen])

  const handleLogout = () => {
    logout()
    setMobileOpen(false)
    navigate('/login')
  }

  const isActive = (path: string) => location.pathname === path

  const navItems: NavItem[] = user
    ? [
        { to: '/', label: 'Home', icon: Home },
        { to: '/deviceManager', label: 'Devices', icon: Waves },
        { to: '/records', label: 'Analytics', icon: BarChart3 },
        { to: '/notifications', label: 'Alerts', icon: Bell, badgeCount: activeCount },
        ...(user.isAdmin ? [{ to: '/admin/programs', label: 'Admin', icon: Shield }] : []),
        { to: '/profile', label: 'Profile', icon: User },
      ]
    : []

  const mobileSheet =
    mobileOpen && user && navPortalEl
      ? createPortal(
          <div
            className="fixed inset-0 z-[200] flex flex-col bg-zinc-900 lg:hidden"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              minHeight: '100%',
              height: '100svh',
              maxHeight: '100svh',
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
          >
            <button
              type="button"
              className="absolute inset-0 bg-black/60"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
            />
            <nav
              className="relative z-[1] flex min-h-0 flex-1 touch-pan-y flex-col overflow-y-auto overscroll-y-contain px-4 shadow-2xl"
              style={{
                WebkitOverflowScrolling: 'touch',
                paddingTop: 'calc(3.5rem + env(safe-area-inset-top, 0px))',
                paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom, 0px))',
              }}
              aria-label="Main menu"
            >
              <div className="mx-auto flex w-full max-w-lg flex-col gap-2">
                {navItems.map((item) => (
                  <NavLinkRow
                    key={item.to}
                    item={item}
                    active={isActive(item.to)}
                    wide
                    onNavigate={() => setMobileOpen(false)}
                  />
                ))}
                <Button
                  variant="outline"
                  className="mt-2 w-full justify-start gap-2 border-emerald-600/50 py-2.5 text-emerald-300 hover:bg-emerald-950/50"
                  onClick={handleLogout}
                >
                  <LogOut className="size-4 shrink-0" aria-hidden />
                  Log out
                </Button>
              </div>
            </nav>
          </div>,
          navPortalEl
        )
      : null

  return (
    <>
      <header className="hydro-nav sticky top-0 z-[60] w-full border-b-2 border-emerald-900/35 bg-zinc-900 text-zinc-100 shadow-md">
        <div className="mx-auto flex min-h-[3.5rem] w-full max-w-[1600px] items-center justify-between gap-4 px-4 py-2 sm:px-6 sm:py-2.5">
          <Link
            to="/"
            className="flex min-w-0 shrink-0 items-center gap-3 text-zinc-100 no-underline hover:text-white"
            onClick={() => setMobileOpen(false)}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md border border-zinc-600 bg-zinc-950 ring-1 ring-zinc-700/80">
              <img src="/logo.png" alt="" width={24} height={24} className="h-6 w-6 object-contain" />
            </span>
            <span className="hidden text-sm font-semibold tracking-tight text-zinc-100 sm:inline">
              Intelli Gardener
            </span>
          </Link>

          {user ? (
            <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
              {/* Desktop: horizontal links */}
              <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
                {navItems.map((item) => (
                  <NavLinkRow key={item.to} item={item} active={isActive(item.to)} />
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-1 gap-2 border-emerald-600/50 px-4 py-2 text-emerald-300 hover:bg-emerald-950/50"
                  onClick={handleLogout}
                >
                  <LogOut className="size-4" aria-hidden />
                  Log out
                </Button>
              </nav>

              {/* Mobile / tablet: menu only */}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-zinc-100 hover:bg-zinc-800 lg:!hidden"
                aria-expanded={mobileOpen}
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                onClick={() => setMobileOpen((o) => !o)}
              >
                {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
              </Button>
            </div>
          ) : (
            <nav className="flex items-center gap-3" aria-label="Account">
              <Button asChild variant="ghost" size="sm" className="px-4 text-zinc-100 hover:bg-zinc-800">
                <Link to="/login" className="no-underline">
                  Log in
                </Link>
              </Button>
              <Button asChild size="sm" className="bg-emerald-600 px-4 text-white hover:bg-emerald-500">
                <Link to="/signup" className="no-underline">
                  Sign up
                </Link>
              </Button>
            </nav>
          )}
        </div>
      </header>
      {mobileSheet}
    </>
  )
}

export default Navbar1
